import { Link, useParams } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "../../../components/ui/breadcrumb";
import {
  type IFavoriteSocial,
  type IFavoriteSocialMinimal,
  useGetFavoritesByIdAdminHttp,
} from "../../../hooks/http/admin/favorites/get-favorites-by-id.admin.http";
import Spinner from "../../../components/spinner.components";
import { debounce, handleHttpError } from "../../../utils/utils";
import { useToast } from "../../../hooks/use-toast";
import { useCallback, useEffect, useState } from "react";
import SearchIcon from "../../../assets/svgs/common/search.svg";
import { Input } from "../../../components/ui/input";
import { XIcon } from "lucide-react";
import { FavoriteTableAdminPartial } from "./favorite-table.admin.partials";
import { useSearchFavoriteSocialAdminHttp } from "../../../hooks/http/admin/favorites/search-social.admin.http";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import { Button } from "../../../components/ui/button";
import DocumentSmall from "../../../assets/svgs/campaigns/doc.svg";
import { FavoriteCsvTableAdminPartial } from "./favorite-csv-table.admin.partials";
import { Separator } from "../../../components/ui/separator";
import InfoIcon from "../../../assets/svgs/common/info.svg";
import millify from "millify";
import GrowthIcon from "../../../assets/svgs/common/growth.svg";
import {
  type IFavoriteMetrics,
  useGetMetricsAdminHttp,
} from "../../../hooks/http/admin/favorites/metrics.admin.http";

export default function FavoriteGroupAdminPartial() {
  const { favoriteId } = useParams();
  const [search, setSearch] = useState("");
  const [typing, setTyping] = useState(false);
  const { toast } = useToast();
  const [favoriteData, setFavoriteData] = useState<IFavoriteSocial>();
  const [data, setData] = useState<IFavoriteSocialMinimal[]>();
  const { execute: getFavoritesByIdHttp, fetching: fetchingFavoritesbyId } =
    useGetFavoritesByIdAdminHttp();
  const {
    execute: searchSocialHttp,
    fetching: fetchingSocials,
    cancel: cancelSearchSocialHttp,
  } = useSearchFavoriteSocialAdminHttp();
  const { execute: getMetricsHttp, fetching: fetchingMetrics } =
    useGetMetricsAdminHttp();

  const [metircs, setMetrics] = useState<IFavoriteMetrics>();

  const [tab, setTab] = useState("socials");

  async function getMetrics() {
    if (!favoriteId) {
      return;
    }
    const response = await getMetricsHttp({
      body: null,
      pathParams: { favoriteId: favoriteId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setMetrics(response.data.metrics);
  }

  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      const searchString = searchValue.trim();
      if (!searchString || !favoriteId) {
        return;
      }
      const response = await searchSocialHttp({
        body: null,
        pathParams: { id: favoriteId },
        queryParams: {
          searchString: searchString,
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }

      setData(
        response.data.favouriteSocial.flatMap((social) =>
          social.socials.map((s) => ({
            social: s.social,
            createdAt: s.createdAt,
            createdBy: s.createdBy,
          }))
        )
      );
      console.log(
        response.data.favouriteSocial.flatMap((social) =>
          social.socials.map((social) => social.social)
        )
      );
      setTyping(false);
    }, 500),
    []
  );

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    cancelSearchSocialHttp();
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
    setTyping(true);
    if (e.target.value.trim().length === 0) {
      setData([]);
      document.getElementById("getSocials")?.click();
    }
  }

  async function getFavoritesById() {
    if (!favoriteId) {
      return;
    }
    const response = await getFavoritesByIdHttp({
      body: null,
      pathParams: { id: favoriteId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setFavoriteData(response.data.favouriteSocial);
    setData(
      response.data.favouriteSocial.socials.map((social) => ({
        social: social.social,
        createdAt: social.createdAt,
        createdBy: social.createdBy,
      }))
    );
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (search.trim().length === 0) {
      getFavoritesById();
    }
    if (search.trim().length) {
      setTab("socials");
    }
  }, [search]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getMetrics();
  }, [favoriteId]);

  const tabs = [
    {
      title: "Socials added",
      value: "socials",
    },
    {
      title: "CSV logs",
      value: "csv",
    },
  ];

  return (
    <div className="flex flex-col gap-10 p-10 pt-14 w-full">
      <div className="flex justify-between items-center">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <Link to="/admin/favorites">
                <h1 className="font-semibold text-secondary text-xl">
                  Favorites
                </h1>
              </Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-secondary" />
            <BreadcrumbItem>
              <h1 className="font-semibold text-primary text-xl">
                {fetchingFavoritesbyId ? (
                  <Spinner width={20} />
                ) : (
                  favoriteData?.name
                )}
              </h1>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-4">
          <Link to={`${window.location.pathname}/import`}>
            <Button className="flex items-center gap-3 bg-tertiarybuttonbg px-3 py-7 border border-border rounded-xl font-semibold">
              <DocumentSmall />
              Upload CSV
            </Button>
          </Link>
          <div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10">
            <SearchIcon />
            <Input
              value={search}
              onChange={(e) => {
                handleSearch(e);
              }}
              className="bg-transparent border-none w-96 font-semibold text-base placeholder:text-secondary"
              placeholder="Search by name, instagram ID, and number"
            />

            {search.trim().length > 0 && (
              <XIcon
                onClick={() => {
                  setSearch("");
                  setData([]);
                  document.getElementById("getSocials")?.click();
                }}
                className="right-5 absolute cursor-pointer"
              />
            )}
          </div>
        </div>
      </div>
      {fetchingMetrics ? (
        <div className="flex justify-center items-center w-full h-52">
          <Spinner width={20} />
        </div>
      ) : (
        <div className="flex flex-col bg-bginput p-4 pb-8 border border-border rounded-xl w-full h-52">
          <div className="flex justify-between items-center px-4 pb-3">
            <p className="font-semibold text-xl">Statistics</p>
            <Link to={`/admin/favorites/${favoriteId}/stats`}>
              <Button className="bg-tertiarybuttonbg px-3 py-2 border border-border font-semibold">
                View detailed stats
              </Button>
            </Link>
          </div>
          <Separator />
          <div className="flex justify-between items-center px-4 pt-8">
            {[
              { heading: "Total Follower", value: metircs?.followers.count },
              {
                heading: "Total count of users",
                value: metircs?.creators.count,
              },
              {
                heading: "Total average view count",
                value: metircs?.views.average,
              },
              {
                heading: "Total engagement rate",
                value: metircs?.engagement.average.toFixed(2),
              },
            ].map((data, index) => (
              <div key={data.heading} className="flex flex-col w-fit text-left">
                <p className="flex items-center gap-2 font-semibold text-base text-secondary">
                  {data.heading}
                  <InfoIcon />
                </p>
                {index === 3 ? (
                  <p className="flex items-center gap-2 font-semibold text-4xl text-primary">
                    {data.value}% <GrowthIcon />{" "}
                  </p>
                ) : (
                  <p className="font-semibold text-4xl">
                    {millify(Number(data.value))}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col w-full">
        <Tabs
          value={tab}
          onValueChange={(value) => {
            setTab(value);
            console.log(value);
          }}
        >
          <TabsList>
            {tabs.map((tab) => {
              return (
                <TabsTrigger
                  key={tab.value}
                  className="px-4 py-2 border-b-2 font-semibold"
                  value={tab.value}
                >
                  {tab.title}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent className="py-6" value={tabs[0].value}>
            {search.trim().length ? (
              <FavoriteTableAdminPartial
                searchFetching={fetchingSocials || typing}
                search={search}
                tableType="search"
				searchData={data}
              />
            ) : (
              <FavoriteTableAdminPartial tableType="favourites" />
            )}
          </TabsContent>
          <TabsContent className="py-6" value={tabs[1].value}>
            <FavoriteCsvTableAdminPartial />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
